import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, FileText, Layers, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const PulpAndPaper = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/pulp-paper"]?.title} description={seoData["industries/pulp-paper"]?.description} path="/industries/pulp-paper" keywords={seoData["industries/pulp-paper"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Pulp & Paper"
        description="Coating and corrosion repair for pulp and paper mills. Chemical-resistant coatings, digestor lining, tank repair, and equipment protection for the pulping and papermaking process."
        backgroundKey="pulp-paper"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['White and black liquor corrosion on digestors and storage tanks',
              'Chlorine dioxide and bleach plant chemical attack',
              'Wet-end machine corrosion and wear',
              'Effluent treatment system deterioration',
              'Steam and condensate pipe corrosion',
              'Concrete floor and containment area degradation'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-emerald-50 rounded-lg">
                <FileText className="text-emerald-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Digestor & Tank Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical-resistant lining systems for digestors, blow tanks, and liquor storage. High-temperature and high-alkalinity resistant coatings for black liquor and green liquor service.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Bleach Plant Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chlorine dioxide and hypochlorite resistant coating systems for washers, towers, and storage tanks in the bleaching sequence.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Machine Room Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Floor coating, equipment rebuild, and piping repair in wet-end and dry-end machine room environments. Anti-slip coatings for safety in wet processing areas.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Effluent System Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Clarifier coating, aeration basin lining, and pipe repair for mill wastewater treatment systems. Chemical-resistant coatings for aggressive mill effluent.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Pulp & Paper?</h2>
              <div className="space-y-3">
                {['Chemical resistance for pulping and bleaching chemicals',
                  'High-temperature coating systems for process applications',
                  'Mill shutdown and outage scheduling experience',
                  'Confined space entry certified crews',
                  'Fast cure systems for time-critical outage work',
                  'Full QA/QC documentation for mill engineering standards'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-emerald-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Pulp & Paper Specialists</h3>
              <p className="text-emerald-100 mb-6">BCS understands the unique chemical environments in pulp and paper mills. Our coatings are selected for specific resistance to the chemicals in your process.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/tank-repair')}>
                View Tank Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Mill Equipment</h2>
          <p className="text-blue-100 mb-8">Contact us to plan coating and repair work around your next mill shutdown.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PulpAndPaper;
